import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { safeParse } from 'secure-json-parse';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { useDateRangeQueryString } from '../../../shared/hooks';
import { useHumioToken } from '../../../shared/providers';
import { EntityInstance, DetectionAlertItem } from '../types';
import {
  useGetEntityDetectionTimelinePaginated,
  useQueryAlertMetadataExcludedEntitiesPaginated,
} from '../../detections/queries';
import { useSimpleSearchQuery } from '../../investigations/queries';
import { EntityDetectionTimelineItem } from '../../detections/queries';
import { useAgenticTriageSettings } from '../hooks/useAgenticTriageSettings';

interface UseEntityAnalysisDetailsQueriesProps {
  selectedEntity?: EntityInstance;
  enabled: boolean; // Only fetch when popover/modal is actually open
  setSelectedEntity?: React.Dispatch<
    React.SetStateAction<EntityInstance | undefined>
  >;
}

export interface UseEntityAnalysisDetailsQueriesResult {
  queriesLoading: boolean;
  isValidatingSuppress: boolean; // Loading state specifically for suppression validation
}

interface QueryFnOptions {
  variables: {
    queryString: string;
    startTime?: number;
    end: string;
    start: string;
    clientSessionId: string | null;
    tenantsForQuery: string[];
  };
  fetchPolicy?: WatchQueryFetchPolicy;
}

/**
 * Lazy-loading hook for EntityAnalysis system queries.
 * Only fetches data when enabled=true to optimize performance in tables.
 */
export const useEntityAnalysisDetailsQueries = ({
  selectedEntity,
  enabled,
  setSelectedEntity,
}: UseEntityAnalysisDetailsQueriesProps) => {
  const { agenticTriageFeatureEnabled } = useAgenticTriageSettings();
  const { token: sessionToken } = useHumioToken();
  const globalDateRange = useDateRangeQueryString();

  // Use entity-specific dates if provided, otherwise use global dates
  const start = selectedEntity?.start ?? globalDateRange.start;
  const end = selectedEntity?.end ?? globalDateRange.end;

  const entityId = selectedEntity?.entityId || '';
  const entityName = selectedEntity?.entityName || '';
  const tenantId = selectedEntity?.tenantInfo?.tenant_id || '';

  const responseIntervalStart = start;
  const oneDayIntervalStart = moment(end * 1000)
    .subtract(1, 'day')
    .unix();
  const sixHourIntervalStart = moment(end * 1000)
    .subtract(6, 'hours')
    .unix();
  const oneMinuteIntervalStart = moment(end * 1000)
    .subtract(1, 'minute')
    .unix();

  /**
   * Normalizes detection data for display in entity information popovers.
   * Includes alert_name, severity, is_custom_severity, and alert_timestamp for display.
   * Uses last_seen timestamp as the observed time for detection cards.
   * Also preserves first_seen and last_seen for proper date sorting.
   */
  const normalizeEntityDetectionItem = (
    detection: EntityDetectionTimelineItem,
  ): DetectionAlertItem => ({
    // Actually used properties
    alert_name: detection.alert_name || '',
    severity: detection.severity ?? 0,
    is_custom_severity: detection.is_custom_severity || false,
    // Use last_seen as the observed timestamp (most recent occurrence)
    alert_timestamp: { observed: detection.last_seen || 0 },
    alert_info: {
      alert_type: detection.alert_type || '',
      alert_name: detection.alert_name || '',
    },
    uid: '',
    alert_entity: {
      entity_name: detection.entity_name || '',
      entity_type: detection.entity_type || '',
    },
    // Preserve first_seen and last_seen for proper date range handling
    // Default to 0 for null handling and falsy checks
    first_seen: detection.first_seen || 0,
    last_seen: detection.last_seen || 0,
  });

  // Detection timeline query
  const shouldSkipTimelineQuery =
    !enabled || !selectedEntity?.timeoutId || !entityId || !tenantId;

  const timelineQueryResult = useGetEntityDetectionTimelinePaginated({
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: shouldSkipTimelineQuery,
    variables: {
      offset: 0,
      start,
      end,
      tenant_id: tenantId,
      entity_id: entityId,
      must_conds: [`{"terms": {"tenant": ["${tenantId}"]}}`],
      // Sort by first_seen ascending to get detections across the entire time range
      // This ensures we get both old and new detections for proper card display
      sort: [{ sort_by: 'first_seen', sort_dir: 'asc' }],
    },
    onCompleted: ({ getEntityDetectionTimelinePaginated }) => {
      // Capture entityId at closure time to prevent race conditions
      const requestEntityId = entityId;
      const { total_items } = getEntityDetectionTimelinePaginated;
      const timelineData = getEntityDetectionTimelinePaginated?.data || [];

      const detectionDataList = timelineData.map(normalizeEntityDetectionItem);

      // Only update if this response is for the current entity
      if (selectedEntity?.entityId === requestEntityId) {
        setSelectedEntity?.((prevState: EntityInstance | undefined) => {
          if (!prevState) return prevState;

          // Try to match the specific detection from timeline based on content_id
          // This ensures we suppress the correct entity-alert association
          const existingContentId = prevState.detectionData?.content_id;
          const matchedDetection = existingContentId
            ? timelineData.find((d) => d.content_id === existingContentId)
            : null;

          // Fall back to first detection if no match found
          const currentDetection = matchedDetection || timelineData[0] || null;

          const updatedDetectionData = currentDetection
            ? {
                ...currentDetection,
                tenant: currentDetection.tenant ?? undefined,
                total_items,
                detection_count: total_items,
                total_detections: total_items,
                content_id: currentDetection?.content_id,
                alert_name: currentDetection?.alert_name,
                alert_type: currentDetection?.alert_type,
                alert_info: currentDetection?.alert_info || {
                  alert_name: currentDetection?.alert_name,
                  alert_type: currentDetection?.alert_type,
                  content_id: currentDetection?.content_id,
                },
                tenant_info: currentDetection?.tenant_info || {
                  tenant_id: currentDetection?.tenant ?? undefined,
                  tenant_display_name: currentDetection?.tenant ?? undefined,
                },
              }
            : prevState.detectionData;

          // Recalculate showSuppressEntity now that we have detection data from timeline
          // Use total_items (actual count from timeline) for the check, not the initial detection_count
          // This matches the old component's behavior
          const actualDetectionCount = total_items || 0;
          const updatedShowSuppressEntity =
            prevState.entityCategory === 'source' &&
            (!prevState.requireSingleDetectionForSuppression ||
              actualDetectionCount === 1);

          // When we get new detection data with content_id, clear both:
          // 1. excludedEntitiesVariables - so it rebuilds with content_id
          // 2. entityIsSuppressed - so loading state shows until new query completes
          const hasNewContentId =
            updatedDetectionData?.content_id &&
            updatedDetectionData.content_id !==
              prevState.detectionData?.content_id;

          return {
            ...prevState,
            detectionData: updatedDetectionData,
            detectionDataList,
            showSuppressEntity: updatedShowSuppressEntity,
            // Clear excludedEntitiesVariables so it gets rebuilt with the new content_id
            // This ensures suppression queries use detection-specific matching
            excludedEntitiesVariables: hasNewContentId
              ? undefined // Will be rebuilt by EntitiesContext with content_id
              : prevState.excludedEntitiesVariables,
            // Also clear entityIsSuppressed to force loading state
            // until the new suppression query with content_id completes
            entityIsSuppressed: hasNewContentId
              ? undefined
              : prevState.entityIsSuppressed,
          };
        });
      }
    },
    onError: () => {
      // Ensure detectionDataList is set even on error to prevent stuck loader
      const requestEntityId = entityId;
      if (selectedEntity?.entityId === requestEntityId) {
        setSelectedEntity?.((prevState: EntityInstance | undefined) =>
          prevState
            ? {
                ...prevState,
                detectionDataList: [],
              }
            : prevState,
        );
      }
    },
  });

  const { loading: getEntityDetectionTimelinePaginatedLoading } =
    timelineQueryResult || {};

  // Note: We don't skip queries for destination entities - they can still have detections
  // The query will run and return whatever data exists for that entity_id

  // Suppression status query
  const { loading: queryAlertMetadataExcludedEntitiesPaginatedLoading } =
    useQueryAlertMetadataExcludedEntitiesPaginated({
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      skip:
        !enabled ||
        !selectedEntity?.timeoutId ||
        !selectedEntity?.excludedEntitiesVariables ||
        !entityId,
      variables: selectedEntity?.excludedEntitiesVariables,
      onCompleted: ({ queryAlertMetadataExcludedEntitiesPaginated }) => {
        const excludedEntities =
          queryAlertMetadataExcludedEntitiesPaginated?.excluded_entities || [];
        const entityIsSuppressed = excludedEntities.length > 0;

        setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
          ...prevState,
          excludeEntitiesData: excludedEntities,
          entityIsSuppressed,
        }));
      },
      onError: ({ extraInfo }) => {
        setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
          ...prevState,
          excludeEntitiesError: extraInfo,
          entityIsSuppressed: false,
        }));
      },
    });

  // Logscale metadata query (for IOC data, ASN, and geographic data)
  // Query based on entity category to get correct geographic data
  const entityCategory = selectedEntity?.entityCategory;
  const isSourceEntity = entityCategory === 'source';

  const queryStringMetadata = isSourceEntity
    ? // Source entity: search where it appears as originator
      `id.orig_h=${entityName}
              |ioc:lookup(field=[id.orig_h], type="ip_address", strict=false, confidenceThreshold="low", include=["published_date", "malicious_confidence"])
              |asn(field=id.orig_h)
              |ipLocation(field=id.orig_h, as=orig_geo)
              |table([id.orig_h.asn, id.orig_h.org, orig_geo.city, orig_geo.state, orig_cc, orig_geo.country, service, ioc[0].published_date, ioc[0].malicious_confidence], limit=1)\n`
    : // Destination entity: search where it appears as responder
      `id.resp_h=${entityName}
              |ioc:lookup(field=[id.resp_h], type="ip_address", strict=false, confidenceThreshold="low", include=["published_date", "malicious_confidence"])
              |asn(field=id.resp_h)
              |ipLocation(field=id.resp_h, as=resp_geo)
              |table([id.resp_h.asn, id.resp_h.org, resp_geo.city, resp_geo.state, resp_cc, resp_geo.country, service, ioc[0].published_date, ioc[0].malicious_confidence], limit=1)\n`;

  const queryStringOriginator = `id.orig_h=${entityName}
              |resp_bytes>0 OR history=/[had]/
              |groupBy(service)
              |table([service], limit=1)`;

  const queryStringResponder = `id.resp_h=${entityName}
              |resp_bytes>0 OR history=/[had]/
              |groupBy(service)
              |table([service], limit=1)`;

  const [simpleSearchQueryMetadata, { loading: isSimpleSearchMetaLoading }] =
    useSimpleSearchQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        // Capture entityId at closure time to prevent race conditions
        const requestEntityId = entityId;

        // Always set logscaleMetaData - even if empty, to stop skeleton loader
        const parsedData = data.simpleSearchQuery?.events[0]
          ? safeParse(data.simpleSearchQuery?.events[0])
          : {};

        // Normalize country code field based on entity category
        const isSource = selectedEntity?.entityCategory === 'source';
        if (isSource && parsedData && !parsedData.orig_cc) {
          parsedData.orig_cc = parsedData['orig_geo.country'];
        } else if (!isSource && parsedData && !parsedData.resp_cc) {
          parsedData.resp_cc = parsedData['resp_geo.country'];
        }

        // Only update if this response is for the current entity
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleMetaData: parsedData,
          }));
        }
      },
      onError: () => {
        // Set empty object on error to stop skeleton loader
        const requestEntityId = entityId;
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleMetaData: {},
          }));
        }
      },
    });

  const [simpleSearchQueryOriginator, { loading: isSimpleSearchOrigLoading }] =
    useSimpleSearchQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        // Capture entityId at closure time to prevent race conditions
        const requestEntityId = entityId;

        // Always set logscaleOrigData - even if empty, to stop skeleton loader
        const parsedData = data.simpleSearchQuery?.events[0]
          ? safeParse(data.simpleSearchQuery?.events[0])
          : {};

        // Only update if this response is for the current entity
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleOrigData: parsedData,
          }));
        }
      },
      onError: () => {
        // Set empty object on error to stop skeleton loader
        const requestEntityId = entityId;
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleOrigData: {},
          }));
        }
      },
    });

  const [simpleSearchQueryResponder, { loading: isSimpleSearchRespLoading }] =
    useSimpleSearchQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        // Capture entityId at closure time to prevent race conditions
        const requestEntityId = entityId;

        // Always set logscaleRespData - even if empty, to stop skeleton loader
        const parsedData = data.simpleSearchQuery?.events[0]
          ? safeParse(data.simpleSearchQuery?.events[0])
          : {};

        // Only update if this response is for the current entity
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleRespData: parsedData,
          }));
        }
      },
      onError: () => {
        // Set empty object on error to stop skeleton loader
        const requestEntityId = entityId;
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleRespData: {},
          }));
        }
      },
    });

  // DHCP query - for DHCP field in entity information panel
  const queryStringDhcp = `#path=dhcp
              | assigned_addr=${entityName}
              | groupBy(field=[client_addr, server_addr, mac, domain, lease_time], function=[selectLast(ts), count(as=request_cnt)])
              | rename(field=[[ts, last_seen]])`;

  const [simpleSearchQueryDhcp, { loading: isSimpleSearchDhcpLoading }] =
    useSimpleSearchQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        // Capture entityId at closure time to prevent race conditions
        const requestEntityId = entityId;

        // Always set logscaleDhcpData - even if empty, to stop skeleton loader
        const parsedData = data.simpleSearchQuery?.events[0]
          ? safeParse(data.simpleSearchQuery?.events[0])
          : {};

        // Only update if this response is for the current entity
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleDhcpData: parsedData,
          }));
        }
      },
      onError: () => {
        // Set empty object on error to stop skeleton loader
        const requestEntityId = entityId;
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleDhcpData: {},
          }));
        }
      },
    });

  // Known_* logs query - for multiple fields in entity information panel
  // Provides: Network Names, Listening Ports, Certificate Subject, User IDs, Host VLAN,
  // Name Services, Available Services, Applications, Software
  const queryStringKnown = `#path=known_*
              | host_ip=${entityName}
              | groupBy(host_ip, function=[collect(fields=[hostname, port, subject, user, host_vlan], limit=10), array:reduceAll("protocols[]", function=collect("Name Services"), var="Name Services"), array:reduceAll("service[]", function=collect("Available Services"), var="Available Services"), array:reduceAll("app[]", function=collect("Applications"), var="Applications"), array:reduceAll("software[]", function=collect("Software"), var="Software")])
              | rename(field=[[host_ip, "IP Address"], [hostname, "Network Names"], [port, "Listening Ports"], [subject, "Certificate Subject"], [user, "User IDs"]])`;

  const [simpleSearchQueryKnown, { loading: isSimpleSearchKnownLoading }] =
    useSimpleSearchQuery({
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        // Capture entityId at closure time to prevent race conditions
        const requestEntityId = entityId;

        // Always set logscaleKnownData - even if empty, to stop skeleton loader
        const parsedData = data.simpleSearchQuery?.events[0]
          ? safeParse(data.simpleSearchQuery?.events[0])
          : {};

        // Only update if this response is for the current entity
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleKnownData: parsedData,
          }));
        }
      },
      onError: () => {
        // Set empty object on error to stop skeleton loader
        const requestEntityId = entityId;
        if (selectedEntity?.entityId === requestEntityId) {
          setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
            ...prevState,
            logscaleKnownData: {},
          }));
        }
      },
    });

  // Sync query loading states to component state
  useEffect(() => {
    setSelectedEntity?.((prevState: EntityInstance | undefined) => ({
      ...prevState,
      isSimpleSearchLoading:
        isSimpleSearchMetaLoading ||
        isSimpleSearchOrigLoading ||
        isSimpleSearchRespLoading ||
        isSimpleSearchDhcpLoading ||
        isSimpleSearchKnownLoading,
      isSimpleSearchMetaLoading,
      isSimpleSearchOrigLoading,
      isSimpleSearchRespLoading,
      isSimpleSearchDhcpLoading,
      isSimpleSearchKnownLoading,
    }));
  }, [
    isSimpleSearchMetaLoading,
    isSimpleSearchOrigLoading,
    isSimpleSearchRespLoading,
    isSimpleSearchDhcpLoading,
    isSimpleSearchKnownLoading,
    setSelectedEntity,
  ]);

  const entityInformationQueriesLoading = useMemo(
    () =>
      isSimpleSearchMetaLoading ||
      isSimpleSearchOrigLoading ||
      isSimpleSearchRespLoading ||
      isSimpleSearchDhcpLoading ||
      isSimpleSearchKnownLoading ||
      getEntityDetectionTimelinePaginatedLoading ||
      queryAlertMetadataExcludedEntitiesPaginatedLoading,
    [
      getEntityDetectionTimelinePaginatedLoading,
      isSimpleSearchOrigLoading,
      isSimpleSearchMetaLoading,
      isSimpleSearchRespLoading,
      isSimpleSearchDhcpLoading,
      isSimpleSearchKnownLoading,
      queryAlertMetadataExcludedEntitiesPaginatedLoading,
    ],
  );

  // Loading state specifically for suppression validation
  // Shows loading spinner while fetching timeline to determine if entity can be suppressed
  const isValidatingSuppress = useMemo(
    () =>
      (getEntityDetectionTimelinePaginatedLoading ||
        queryAlertMetadataExcludedEntitiesPaginatedLoading) &&
      selectedEntity?.entityCategory === 'source',
    [
      getEntityDetectionTimelinePaginatedLoading,
      queryAlertMetadataExcludedEntitiesPaginatedLoading,
      selectedEntity?.entityCategory,
    ],
  );

  // Wrap query functions to pass variables at call-time instead of using hook options
  // This prevents stale variables from being used when start/end change during navigation
  const callQueryWithVariables = useCallback(
    (
      queryFn: (options: QueryFnOptions) => Promise<unknown>,
      queryString: string,
      startTime: number,
    ) => {
      return () => {
        // Skip if not enabled
        if (!enabled || !entityName || !tenantId) {
          return Promise.resolve();
        }

        // Capture current values at call time
        const currentEnd = end;
        const currentStart = start;

        // Validate required parameters
        const isValidDateRange =
          currentEnd > 0 && currentStart > 0 && currentStart < currentEnd;
        const isValidQuery = !!queryString?.trim();
        const isValidTenant = !!tenantId?.trim();

        if (!isValidDateRange || !isValidQuery || !isValidTenant) {
          console.warn('Skipping query due to invalid parameters:', {
            currentEnd,
            currentStart,
            queryString: queryString?.substring(0, 50),
            tenantId: tenantId || '(empty)',
          });
          return Promise.resolve();
        }

        return queryFn({
          variables: {
            clientSessionId: sessionToken || null,
            queryString,
            end: currentEnd.toString(),
            start: startTime.toString(),
            tenantsForQuery: [tenantId],
          },
          fetchPolicy: 'cache-first',
        });
      };
    },
    [enabled, end, start, sessionToken, tenantId, entityName],
  );

  // Execute the LogScale queries when enabled
  useEffect(() => {
    if (enabled && entityName && tenantId && selectedEntity?.timeoutId) {
      // Execute metadata query
      callQueryWithVariables(
        simpleSearchQueryMetadata,
        queryStringMetadata,
        oneMinuteIntervalStart,
      )();

      // Execute originator query
      callQueryWithVariables(
        simpleSearchQueryOriginator,
        queryStringOriginator,
        sixHourIntervalStart,
      )();

      // Execute responder query
      callQueryWithVariables(
        simpleSearchQueryResponder,
        queryStringResponder,
        sixHourIntervalStart,
      )();

      if (agenticTriageFeatureEnabled) {
        // Execute DHCP query
        callQueryWithVariables(
          simpleSearchQueryDhcp,
          queryStringDhcp,
          oneDayIntervalStart,
        )();

        // Execute Known query
        callQueryWithVariables(
          simpleSearchQueryKnown,
          queryStringKnown,
          oneDayIntervalStart,
        )();
      }
    }
  }, [
    enabled,
    entityName,
    tenantId,
    selectedEntity?.timeoutId,
    selectedEntity?.entityId,
    callQueryWithVariables,
    simpleSearchQueryMetadata,
    queryStringMetadata,
    responseIntervalStart,
    simpleSearchQueryOriginator,
    queryStringOriginator,
    sixHourIntervalStart,
    simpleSearchQueryResponder,
    queryStringResponder,
    simpleSearchQueryDhcp,
    queryStringDhcp,
    simpleSearchQueryKnown,
    queryStringKnown,
  ]);

  return {
    queriesLoading: entityInformationQueriesLoading,
    isValidatingSuppress,
  };
};
